import { Copy, X } from "lucide-react";
import type { CSSProperties, JSX, ReactNode } from "react";
import { cx } from "../cx";

export interface InspectorPanelProps {
  /** Mono title (usually an event or entity id). */
  title?: ReactNode | undefined;
  subtitle?: ReactNode | undefined;
  /** Renders the close button in the title bar. */
  onClose?: (() => void) | undefined;
  /** Overrides the default var(--studio-inspector-w) width. */
  width?: number | string | undefined;
  /** Accessible name for the complementary landmark. */
  "aria-label"?: string | undefined;
  children?: ReactNode | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

/**
 * Docked right-hand detail panel: mono title bar with optional close, then a
 * scrollable body composed from InspectorSection and KVRow. The panel is its
 * own complementary aside landmark so surfaces can dock it anywhere. Ported
 * from design/tarski-kit/components/studio/InspectorPanel.jsx.
 */
export function InspectorPanel({
  title,
  subtitle,
  onClose,
  width,
  "aria-label": ariaLabel,
  children,
  className,
  style,
}: InspectorPanelProps): JSX.Element {
  return (
    <aside
      aria-label={ariaLabel ?? "Inspector"}
      className={cx("tk-inspector", className)}
      style={{ ...(width === undefined ? null : { width }), ...style }}
    >
      <header className="tk-inspector__header">
        <div className="tk-inspector__heading">
          <div className="tk-inspector__title">{title}</div>
          {subtitle === undefined ? null : <div className="tk-inspector__subtitle">{subtitle}</div>}
        </div>
        {onClose === undefined ? null : (
          <button
            type="button"
            className="tk-inspector__close"
            aria-label="Close panel"
            onClick={onClose}
          >
            <X size={16} aria-hidden="true" />
          </button>
        )}
      </header>
      <div className="tk-inspector__body">{children}</div>
    </aside>
  );
}

export interface InspectorSectionProps {
  /** Caps-tracked section heading ("Event data"). */
  label?: ReactNode | undefined;
  /** Optional count rendered in mono after the heading. */
  count?: number | string | undefined;
  children?: ReactNode | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

/** Caps-labeled section inside an InspectorPanel. */
export function InspectorSection({
  label,
  count,
  children,
  className,
  style,
}: InspectorSectionProps): JSX.Element {
  return (
    <section className={cx("tk-inspector-section", className)} style={style}>
      {label === undefined ? null : (
        <h4 className="tk-inspector-section__heading">
          {label}
          {count === undefined ? null : (
            <>
              {/* Explicit space keeps the accessible name "Event data 4";
                  the flex layout ignores the whitespace box. */}{" "}
              <span className="tk-inspector-section__count">{count}</span>
            </>
          )}
        </h4>
      )}
      {children}
    </section>
  );
}

export interface KVRowProps {
  /** Key column text; also names the copy button ("Copy <label>"). */
  label: string;
  /** Value in mono (default true) — hashes, capabilities, ids. */
  mono?: boolean | undefined;
  /** Wrap long values instead of truncating. */
  wrap?: boolean | undefined;
  /** Shows a copy button on hover/focus that writes the value to the clipboard. */
  copyable?: boolean | undefined;
  /** Text written on copy; defaults to the value when it is a plain string. */
  copyValue?: string | undefined;
  children?: ReactNode | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

/**
 * Key-value row: muted key column plus a body or mono value. With `copyable`
 * a copy button appears on hover/focus and writes the value through
 * navigator.clipboard. Ported from
 * design/tarski-kit/components/studio/InspectorPanel.jsx.
 */
export function KVRow({
  label,
  mono = true,
  wrap = false,
  copyable = false,
  copyValue,
  children,
  className,
  style,
}: KVRowProps): JSX.Element {
  const text = copyValue ?? (typeof children === "string" ? children : undefined);
  return (
    <div className={cx("tk-kv-row", className)} style={style}>
      <div className="tk-kv-row__key">{label}</div>
      <div
        className={cx(
          "tk-kv-row__value",
          mono && "tk-kv-row__value--mono",
          wrap && "tk-kv-row__value--wrap",
        )}
      >
        {children}
      </div>
      {copyable && text !== undefined ? (
        <button
          type="button"
          className="tk-kv-row__copy"
          aria-label={`Copy ${label}`}
          onClick={() => {
            void Promise.resolve(navigator.clipboard.writeText(text)).catch(() => {
              // Clipboard access can be denied; the affordance stays quiet.
            });
          }}
        >
          <Copy size={16} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}
